import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { num } from "@/lib/format";

const TONE = {
  HIGH: "border-red-300 bg-red-50 text-red-800",
  MEDIUM: "border-amber-300 bg-amber-50 text-amber-800",
  INFO: "border-blue-300 bg-blue-50 text-blue-800",
};

export const RiskDialog = ({ open, onOpenChange, flags }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl bg-white" data-testid="risk-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-base">Risico's uit de advertentie</DialogTitle>
      </DialogHeader>
      {!flags ? null : (
        <div className="max-h-[70vh] space-y-3 overflow-y-auto">
          {!flags.text_analysed && (
            <p className="border border-slate-200 px-3 py-2 text-[12px] text-slate-500">
              {flags.message}
            </p>
          )}
          {(flags.flags || []).length === 0 ? (
            <div className="flex items-center gap-2 border border-emerald-200 bg-emerald-50 px-3 py-3 text-[12.5px] text-emerald-800">
              <ShieldCheck className="h-4 w-4" /> Geen risicosignalen gevonden in deze advertentie.
            </div>
          ) : (
            <div className="border border-slate-200" data-testid="risk-dialog-flags">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-100 px-3 py-1.5">
                <span className="label-xs">
                  {num((flags.counts || {}).high)} hoog · {num((flags.counts || {}).medium)} middel ·{" "}
                  {num((flags.counts || {}).info)} informatief
                </span>
                <span className="chip border-slate-300 bg-white text-slate-700">
                  Totale aftrek {num(flags.total_penalty, { decimals: 1 })} punten
                </span>
              </div>
              {flags.flags.map((f) => (
                <div key={f.code} className="border-b border-slate-100 px-3 py-2" data-testid={`risk-flag-${f.code}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`chip ${TONE[f.severity] || TONE.INFO}`}>
                      <ShieldAlert className="mr-1 h-3 w-3" />
                      {f.severity === "HIGH" ? "Hoog" : f.severity === "MEDIUM" ? "Middel" : "Info"}
                    </span>
                    <span className="text-[12.5px] font-semibold text-slate-800">{f.label}</span>
                    <span className="chip border-slate-300 bg-slate-100 text-slate-600">
                      {(f.matched_terms || [f.matched_term]).slice(0, 3).join(" · ")}
                    </span>
                    {f.penalty ? (
                      <span className="mono text-[11px] font-semibold text-red-700">
                        −{num(f.penalty, { decimals: 1 })} punten
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-500">geen score-aftrek</span>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] italic leading-snug text-slate-600">
                    “{f.evidence}”
                  </p>
                  {(f.extra_evidence || []).map((e) => (
                    <p key={e} className="mt-0.5 text-[10.5px] italic leading-snug text-slate-400">
                      “{e}”
                    </p>
                  ))}
                </div>
              ))}
            </div>
          )}

          {(flags.positive_signals || []).length > 0 && (
            <div className="border border-emerald-200" data-testid="risk-dialog-positives">
              <div className="label-xs border-b border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-900">
                Positieve signalen in dezelfde advertentie
              </div>
              {flags.positive_signals.map((p) => (
                <div key={p.code || p.label} className="border-b border-emerald-100 px-3 py-1.5">
                  <div className="text-[12px] font-semibold text-slate-800">{p.label}</div>
                  <p className="text-[10.5px] italic text-slate-500">“{p.evidence}”</p>
                </div>
              ))}
            </div>
          )}

          {(flags.suppressed || []).length > 0 && (
            <div className="border border-slate-200" data-testid="risk-dialog-suppressed">
              <div className="label-xs border-b border-slate-200 bg-slate-50 px-3 py-1.5">
                Niet gemarkeerd — advertentie stelt expliciet het tegendeel
              </div>
              {flags.suppressed.map((sp) => (
                <div key={sp.code || sp.label} className="border-b border-slate-100 px-3 py-1.5">
                  <div className="text-[12px] text-slate-700">{sp.label}</div>
                  <p className="text-[10.5px] italic text-slate-500">“{sp.evidence}”</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default RiskDialog;
